import { Component, HostBinding, inject, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { FormService } from '@app/services/form.service';
import { Expenditure } from '@app/class/expenditure.class';
import { ExpenditureTypeEnum } from '@app/enums/expenditure-type.enum';
import { ExpenditureInterface } from '@app/interfaces/expenditure.interface';
import { FormUtils } from '@app/utils/form-utils';
import { ExpenditureSourceTypeEnum } from '@app/enums/expenditure-source-type.enum';
import { Subject } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { HasErrorDirective } from '@app/directives/validation/has-error.directive';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { HypoRadioComponent } from '@app/components/form-components/hypo-radio/hypo-radio.component';
import { HypoInputComponent } from '@app/components/form-components/hypo-input/hypo-input.component';
import { HypoSelectComponent } from '@app/components/form-components/hypo-select/hypo-select.component';
import { ShouldShowInputPipe } from '@app/pipes/should-show-input.pipe';
import { ListByTypePipe } from '@app/pipes/list-by-type-pipe/list-by-type.pipe';
import { OptionPipe } from '@app/pipes/option-pipe/option.pipe';
import { RichFormControlDirective } from '@app/directives/validation/rich-form-control.directive';

@Component({
    selector: 'app-expenditure-dialog',
    templateUrl: './expenditure-dialog.component.html',
    styleUrls: ['./expenditure-dialog.component.scss'],
    imports: [
        MatIconModule,
        HasErrorDirective,
        RichFormControlDirective,
        MatDialogModule,
        MatSlideToggleModule,
        ListByTypePipe,
        ReactiveFormsModule,
        HypoRadioComponent,
        HypoInputComponent,
        HypoSelectComponent,
        ShouldShowInputPipe,
        OptionPipe,
    ],
})
export class ExpenditureDialogComponent implements OnInit, OnDestroy {
    dialogRef = inject<MatDialogRef<ExpenditureDialogComponent>>(MatDialogRef);
    data = inject(MAT_DIALOG_DATA);

    protected readonly formService = inject(FormService);

    protected destroyed$: Subject<void> = new Subject<void>();

    public blurContent = false;
    public expenditure: ExpenditureInterface;
    public expenditureDialogForm: UntypedFormGroup;
    public ExpenditureSourceTypeEnum: typeof ExpenditureSourceTypeEnum = ExpenditureSourceTypeEnum;

    @HostBinding('class.show-more') showMore = true;
    public expenditureConfig: Expenditure = new Expenditure();

    get type() {
        return this.expenditureDialogForm.get('type') as UntypedFormArray;
    }

    get sourceType() {
        return this.expenditureDialogForm.get('source.type') as UntypedFormArray;
    }

    public close(data?: any) {
        this.dialogRef.close(data);
    }

    ngOnInit() {
        this.expenditure = this.data?.exp?.value;
        this.showMore = this.data?.showMore;
        this.expenditureDialogForm = this.formService.createExpenditureForm(this.expenditure);
        this.bindValueChanges();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public shouldShowInput(inputName: string) {
        return FormUtils.shouldShowInput(this.expenditureConfig.showInputOnTypes, inputName, this.type.value);
    }

    continue() {
        if (this.formService.hasValidVisibleControls(this.expenditureDialogForm, true)) {
            if (this.sourceType.value === ExpenditureSourceTypeEnum.bank) {
                this.expenditureDialogForm
                    .get('source.name')
                    .patchValue(this.expenditureDialogForm.get('source.bankCode').value);
            }
            if (this.sourceType.value === ExpenditureSourceTypeEnum.country) {
                this.expenditureDialogForm
                    .get('source.name')
                    .patchValue(this.expenditureDialogForm.get('source.countryCode').value);
            }
            this.close(this.expenditureDialogForm);
        }
    }

    private bindValueChanges(): void {
        this.type.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((type: ExpenditureTypeEnum) => {
            if (type === ExpenditureTypeEnum.alimony) {
                this.expenditureDialogForm.get('source.type').patchValue(ExpenditureSourceTypeEnum.other);
                this.expenditureDialogForm.get('source.name').patchValue('Vyživovatel');
            } else {
                if (this.expenditureDialogForm.get('source.name').value === 'Vyživovatel') {
                    this.expenditureDialogForm.get('source.name').patchValue('');
                }
            }
        });

        const sourceType = this.sourceType.value;
        if (sourceType === ExpenditureSourceTypeEnum.bank) {
            this.expenditureDialogForm
                .get('source.bankCode')
                .patchValue(this.expenditureDialogForm.get('source.name').value);
        }

        if (sourceType === ExpenditureSourceTypeEnum.country) {
            this.expenditureDialogForm
                .get('source.countryCode')
                .patchValue(this.expenditureDialogForm.get('source.name').value);
        }

        this.expenditureDialogForm
            .get('source.type')
            .valueChanges.pipe(takeUntil(this.destroyed$))
            .subscribe((type: string) => {
                if (type === ExpenditureSourceTypeEnum.nonBank || type === ExpenditureSourceTypeEnum.country) {
                    this.expenditureDialogForm.get('source.name').patchValue('');
                }
            });
    }
}
